<template>
    <emp-component-modal @modal:close="onClose" :isActive="isActive">
        <div slot="modal-header">Find Facility</div>
        <div slot="modal-body">
            <!-- Step 1 -->
            <div class="modal-body-content" v-show="step === 1">
                <div class="card">
                    <header class="card-header">
                        <p class="card-header-title">Search by Facility Name:</p>
                    </header>
                    <div class="card-content">
                        <div class="columns is-multiline">
                            <emp-textbox label="Name" v-model="name" class="column is-full" />
                        </div>
                    </div>
                    <footer class="card-footer">
                        <e-button
                            :show-loader="searching"
                            @click="submitSearchByName"
                            class="card-footer-item"
                            label="Search"
                        />
                    </footer>
                </div>
                <div class="card">
                    <header class="card-header">
                        <p class="card-header-title">Search by Address:</p>
                    </header>
                    <div class="card-content">
                        <div class="columns is-multiline">
                            <emp-textbox label="Street" v-model="streetAddress" class="column is-6" />
                            <emp-textbox label="Zipcode" v-model="zipcode" class="column is-6" />
                            <emp-textbox label="City" v-model="city" class="column is-6" />
                            <emp-textbox label="Practitioner" v-model="practitioner" class="column is-6" />
                            <select-state v-model="stateId" class="column is-6" />
                            <emp-textbox label="Practitioner Email" v-model="practitionerEmail" class="column is-6" />
                        </div>
                    </div>
                    <footer class="card-footer">
                        <e-button
                            :show-loader="searching"
                            @click="submitSearch"
                            :disabled="!this.hasSearchableValues"
                            class="card-footer-item"
                            label="Search"
                        />
                    </footer>
                </div>
                <div class="columns">
                    <div class="column is-6">
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title">Current Shipping Address:</p>
                            </header>
                            <div class="card-content">
                                <div class="column is-full">
                                    {{ initialShippingAddressFormatted }}
                                </div>
                            </div>
                            <footer class="card-footer">
                                <e-button
                                    :show-loader="searching"
                                    @click="submitSearchWith(initialShippingAddress)"
                                    class="card-footer-item"
                                    label="Use as Search"
                                />
                            </footer>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title">Current Cost Center:</p>
                            </header>
                            <div class="card-content">
                                <div class="column is-full">
                                    {{ initialCostCenterFormatted }}
                                </div>
                            </div>
                            <footer class="card-footer">
                                <e-button
                                    :show-loader="searching"
                                    @click="submitSearchWith(initialCostCenter)"
                                    class="card-footer-item"
                                    label="Use as Search"
                                />
                            </footer>
                        </div>
                    </div>
                </div>
                <div class="card">
                    <header class="card-header">
                        <p class="card-header-title"> Results: </p>
                    </header>
                    <div class="card-content" style="min-height: 200px; max-height: 600px; overflow-y: scroll">
                        <ul class="search-results" v-show="searchResults.length">
                            <li
                                v-for="searchResult in searchResults"
                                :key="searchResult.id"
                                :class="['search-result', { active: searchResult.id === selectedFacility.id }]"
                                @click="selectFacility(searchResult)"
                            >
                                {{ searchResult.name }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <!-- Step 2 -->
            <div class="messate-body-content" v-show="step === 2">
                <div class="columns is-multiline">
                    <div class="column is-full">
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title">Instructions:</p>
                            </header>
                            <div class="card-content">
                                <p>
                                    Select the Ship To, Cost Center, Practitioner and Patient to use for this Order
                                    Line, or the entire Order.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="column is-full">
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title">Shipping Address: {{ initialShippingAddressFormatted }}</p>
                            </header>
                            <div class="card-content" v-show="!facilityHasInitialShipTo">
                                <div class="notification is-warning">
                                    <strong>Shipping Address Not Found.</strong>
                                    <p> The submitted shipping address could not be matched in our system. </p>
                                    <p>
                                        This can happen if the submitted address information is slightly off, please
                                        select the correct ship to or instruct the system to transfer the submitted
                                        address to the selected Facility.
                                    </p>
                                    <p>
                                        <label
                                            ><strong>Transfer to Facility?</strong>
                                            <input type="checkbox" v-model="transferShippingAddress"
                                        /></label>
                                    </p>
                                </div>
                            </div>
                            <div class="card-content">
                                <select-shipping-address
                                    label="Change To:"
                                    v-if="selectedFacility.id"
                                    :name-callback="formatAddressOption"
                                    v-model="selectedShippingAddressId"
                                    :facility-id="selectedFacility.id"
                                />
                            </div>
                        </div>
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title">Cost Center: {{ initialCostCenterFormatted }}</p>
                            </header>
                            <div class="card-content" v-show="!facilityHasInitialCostCenter">
                                <div class="notification is-warning">
                                    <strong>Cost Center Not Found.</strong>
                                    <p> The submitted cost center could not be matched in our system. </p>
                                    <p>
                                        This can happen if the submitted address information is slightly off, please
                                        select the correct ship to or instruct the system to transfer the submitted
                                        address to the selected Facility.
                                    </p>
                                    <p>
                                        <label
                                            ><strong>Transfer to Facility?</strong>
                                            <input type="checkbox" v-model="transferCostCenter"
                                        /></label>
                                    </p>
                                </div>
                            </div>
                            <div class="card-content">
                                <select-cost-center
                                    label="Change To:"
                                    v-if="selectedFacility.id"
                                    :name-callback="formatAddressOption"
                                    v-model="selectedCostCenterId"
                                    :facility-id="selectedFacility.id"
                                />
                            </div>
                        </div>
                        <div class="card">
                            <header class="card-header">
                                <p class="card-header-title"
                                    >Practitioner: {{ initialPractitioner.name }} ({{ initialPractitioner.email }})</p
                                >
                            </header>
                            <div class="card-content" v-show="!facilityHasInitialPractitioner">
                                <div class="notification is-warning">
                                    <strong>Practitioner Not Found.</strong>
                                    <p> The submitted practitioner could not be matched in our system. </p>
                                    <p>
                                        This can happen if the submitted practitioner information is slightly off,
                                        please select the correct ship to or instruct the system to transfer the
                                        submitted practitioner to the selected Facility.
                                    </p>
                                    <p>
                                        <label
                                            ><strong>Transfer to Facility?</strong>
                                            <input type="checkbox" v-model="transferPractitioner"
                                        /></label>
                                    </p>
                                </div>
                            </div>
                            <div class="card-content">
                                <select-practitioner
                                    label="Change To:"
                                    v-if="selectedFacility.id"
                                    :name-callback="formatPractitionerOption"
                                    v-model="selectedPractitionerId"
                                    :facility-id="selectedFacility.id"
                                />
                            </div>
                        </div>
                        <div class="card" v-if="initialPatient">
                            <header class="card-header">
                                <p class="card-header-title"
                                    >Patient: {{ initialPatient.fullNameWithFacilityPatientId }}</p
                                >
                            </header>
                            <div class="card-content" v-show="!facilityHasInitialPatient">
                                <div class="notification is-warning">
                                    <strong>Patient Not Found.</strong>
                                    <p>The submitted Patient could not be matched in our system.</p>
                                    <p>
                                        This can happen if the submitted Patient Information is slightly off, please
                                        select the correct Patient or instruct the system to transfer the submitted
                                        Patient to the selected Facility.
                                    </p>
                                    <p>
                                        <label>
                                            <strong>Transfer to Facility</strong>
                                            <input type="checkbox" v-model="transferPatient" />
                                        </label>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="message-body-buttons" v-show="step === 1">
                <div class="level">
                    <div class="level-left"></div>
                    <div class="level-right">
                        <div class="level-item">
                            <e-button
                                label="Next"
                                @click="onNext"
                                :show-loader="saving"
                                :disabled="!selectedFacility.id"
                            />
                        </div>
                        <div class="level-item">
                            <e-button label="Cancel" buttonClass="is-danger" @click="onClose" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="message-body-buttons" v-show="step === 2">
                <div class="level">
                    <div class="level-left"></div>
                    <div class="level-right">
                        <div class="level-item">
                            <e-button label="Previous" @click="step--" />
                        </div>
                        <div class="level-item">
                            <e-button label="Update Line" @click="onSave" :show-loader="saving" />
                            <emp-tooltip>
                                <p slot="tooltip"
                                    >This button will only update the Order Line that this tool was launched from.</p
                                >
                            </emp-tooltip>
                        </div>
                        <div class="level-item">
                            <e-button label="Update Order" @click="onSave(true)" :show-loader="saving" />
                            <emp-tooltip>
                                <p slot="tooltip"
                                    >This button will update all Order Lines that have the same Order ID as the Order
                                    Line that this tool was launched from.</p
                                >
                            </emp-tooltip>
                        </div>
                        <div class="level-item">
                            <e-button label="Cancel" buttonClass="is-danger" @click="onClose" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </emp-component-modal>
</template>

<style lang="scss" scoped>
.search-results {
    border: solid 1px #7b7e85;
}
.search-result {
    border-bottom: solid 1px #7b7e85;
    padding: 1rem;

    font-size: 2rem;
    cursor: pointer;

    &.active {
        background-color: #393d49;
        color: #eceded;
    }
}

.search-result:not(.active):hover {
    background-color: #eceded;
}

.search-result:last-child {
    border-bottom: none;
}
</style>

<script>
import { mapState, mapActions } from 'vuex';
const Textbox = () => import(/* webpackChunkName: "common" */ 'component/shared/elements/Textbox.vue');
const Button = () => import(/* webpackChunkName: "common" */ 'component/shared/elements/Button.vue');
import SelectState from 'component/shared/elements/SelectState.vue';
import Checkbox from 'component/shared/elements/Checkbox.vue';
import SmartAPI from 'SmartApi';
import SelectShippingAddress from 'component/shared/elements/SelectShippingAddress.vue';
import SelectCostCenter from 'component/shared/elements/SelectCostCenter.vue';
import SelectPractitioner from 'component/shared/elements/SelectPractitioner.vue';
import { getStateCodeById } from 'enumHelpers';
import { systemNotifications } from 'component/shared/elements/mixins';
import { apis } from 'api/client';
import store from 'store';
import { GET_DATA_INCLUDES } from 'ordersConstants';

export default {
    mixins: [systemNotifications],
    props: {
        storePathPrefix: {
            type: String,
            required: true,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        Textbox,
        SelectState,
        'e-button': Button,
        Checkbox,
        SelectShippingAddress,
        SelectCostCenter,
        SelectPractitioner,
    },
    data() {
        return {
            step: 1,
            saving: false,
            searching: false,
            name: '',
            streetAddress: '',
            zipcode: '',
            city: '',
            practitioner: '',
            stateId: 0,
            practitionerEmail: '',
            initialShippingAddress: {},
            initialCostCenter: {},
            initialPractitioner: {},
            initialPatient: {},
            searchResults: [],
            selectedFacility: {},
            transferShippingAddress: 0,
            transferCostCenter: 0,
            transferPractitioner: 0,
            transferPatient: 0,
            selectedShippingAddressId: null,
            selectedCostCenterId: null,
            selectedPractitionerId: null,
            selectedPatientId: null,
        };
    },
    computed: {
        ...mapState({
            modalData: (state) => state.shared.modal.data,
        }),
        selectedFacilityAddresses() {
            return this.selectedFacility?.addresses ?? [];
        },
        selectedFacilityUsers() {
            return this.selectedFacility?.users ?? [];
        },
        selectedFacilityPatients() {
            return this.selectedFacility?.patientNames ?? [];
        },
        facilityHasInitialPatient() {
            return this.selectedFacilityPatients.some((patient) => {
                return patient.fullNameWithFacilityPatientId == this.initialPatient.fullNameWithFacilityPatientId;
            });
        },
        facilityHasInitialShipTo() {
            return this.selectedFacilityAddresses.some((address) => {
                return (
                    address.streetAddress == this.initialShippingAddress.streetAddress &&
                    address.city == this.initialShippingAddress.city &&
                    address.stateId == this.initialShippingAddress.state?.id &&
                    address.zipcode == this.initialShippingAddress.zipcode
                );
            });
        },
        facilityHasInitialCostCenter() {
            return this.selectedFacilityAddresses.some((address) => {
                return (
                    address.streetAddress == this.initialCostCenter.streetAddress &&
                    address.city == this.initialCostCenter.city &&
                    address.stateId == this.initialCostCenter.state?.id &&
                    address.zipcode == this.initialCostCenter.zipcode
                );
            });
        },
        facilityHasInitialPractitioner() {
            return this.selectedFacilityUsers.some((practitioner) => {
                return (
                    practitioner.name == this.initialPractitioner.name ||
                    practitioner.email == this.initialPractitioner.email
                );
            });
        },
        initialShippingAddressFormatted() {
            return `${this.initialShippingAddress.streetAddress}, ${this.initialShippingAddress.city}, ${this.initialShippingAddress.state.code} ${this.initialShippingAddress.zipcode}`;
        },
        initialCostCenterFormatted() {
            return `${this.initialCostCenter.streetAddress}, ${this.initialCostCenter.city}, ${this.initialCostCenter.state.code} ${this.initialCostCenter.zipcode}`;
        },
        hasSearchableValues() {
            let params = {
                streetAddress: this.streetAddress,
                zipcode: this.zipcode,
                city: this.city,
                stateId: this.stateId,
                practitioner: this.practitioner,
                practitionerEmail: this.practitionerEmail,
            };

            return Object.values(params).some(Boolean);
        },
    },
    methods: {
        ...mapActions({
            closeModal: 'shared/modal/closeModal',
        }),
        formatAddressOption(address) {
            let physicalAddressAddressInformation = this.selectedFacility?.addresses?.find((a) => a.id == address.id);

            if (physicalAddressAddressInformation) {
                return `${physicalAddressAddressInformation.streetAddress}, ${
                    physicalAddressAddressInformation.city
                }, ${getStateCodeById(physicalAddressAddressInformation.stateId)} ${
                    physicalAddressAddressInformation.zipcode
                } (${physicalAddressAddressInformation.name})`;
            }

            return addrress.name;
        },
        formatPractitionerOption(practitioner) {
            return `${practitioner.name} (${practitioner.email})`;
        },
        updateLines(params) {
            return this.$store.dispatch(`${this.storePathPrefix}/updateLines`, params);
        },
        onClose() {
            this.closeModal();
        },
        selectFacility(facility) {
            this.selectedFacility = facility;

            if (this.facilityHasInitialShipTo) {
                this.selectedShippingAddressId = this.initialShippingAddress?.id;
            }

            if (this.facilityHasInitialCostCenter) {
                this.selectedCostCenterId = this.initialCostCenter?.id;
            }

            if (this.facilityHasInitialPractitioner) {
                this.selectedPractitionerId = this.practitioner?.id;
            }

            if (this.facilityHasInitialPatient) {
                this.selectedPatientId = this.initialPatient.id;
            }
        },
        async submitSearchByName() {
            if (this.searching) return;

            if (!this.name.trim()) return;

            this.selectedFacility = {};
            this.searching = true;

            let params = {
                name: this.name.trim(),
            };

            let results = await SmartAPI.get({
                routeName: 'finderFacilitySearch',
                routeParams: params,
                config: {
                    hideLoader: true,
                },
            });

            this.searchResults = results?.data ?? [];

            this.searching = false;
        },
        async submitSearchWith(addressInformation) {
            this.streetAddress = addressInformation?.streetAddress;
            this.zipcode = addressInformation?.zipcode;
            this.city = addressInformation?.city;
            this.stateId = addressInformation?.state?.id;

            this.practitioner = '';
            this.practitionerEmail = '';

            await this.submitSearch();
        },
        async submitSearch() {
            if (this.searching) return;

            if (!this.hasSearchableValues) return;

            this.selectedFacility = {};
            this.searching = true;

            let params = {
                streetAddress: this.streetAddress,
                zipcode: this.zipcode,
                city: this.city,
                stateId: this.stateId,
                practitioner: this.practitioner,
                practitionerEmail: this.practitionerEmail,
            };

            let results = await SmartAPI.get({
                routeName: 'finderFacilitySearch',
                routeParams: params,
                config: {
                    hideLoader: true,
                },
            });

            this.searchResults = results?.data ?? [];

            this.searching = false;
        },
        onNext() {
            this.step = 2;
        },
        async onSave(entire_order = false) {
            this.saving = true;
            let line = this.modalData.line;

            try {
                let response = await SmartAPI.post({
                    routeName: 'finderFacilityTransfer',
                    data: {
                        line_id: line.id,
                        order_id: line.order_id,
                        transfer_cost_center: this.trasnferCostCenter,
                        transfer_shipping_address: this.transferShippingAddress,
                        transfer_practitioner: this.transferPractitioner,
                        transfer_patient: this.transferPatient,
                        facility_id: this.selectedFacility.id,
                        cost_center_id: this.selectedCostCenterId,
                        shipping_address_id: this.selectedShippingAddressId,
                        practitioner_id: this.selectedPractitionerId,
                        initial_cost_center_id: this.initialCostCenter.id,
                        initial_shipping_adress_id: this.initialShippingAddress.id,
                        initial_practitioner_id: this.initialPractitioner.id,
                        initial_patient_id: this.initialPatient?.id,
                        entire_order,
                        includes: GET_DATA_INCLUDES,
                    },
                });

                response?.data?.data?.forEach((newLine) => {
                    store.commit(`${this.storePathPrefix}/removeLineFromUnsavedList`, newLine.id);
                    store.commit(`${this.storePathPrefix}/setOriginalLine`, { ...newLine });
                    store.commit(`${this.storePathPrefix}/setMutableLine`, _.cloneDeep(newLine));
                    store.dispatch('shared/quickbar/updateItem', { ...newLine });
                });
            } catch (e) {
                this.showDangerNotification(e);
            } finally {
                this.saving = false;
            }

            this.onClose();
        },
    },
    watch: {
        isActive: {
            handler(value) {
                if (value) {
                    this.initialShippingAddress = this.modalData?.shippingAddress;
                    this.initialCostCenter = this.modalData?.costCenter;
                    this.initialPractitioner = this.modalData?.practitioner;
                    this.initialPatient = this.modalData?.patient;
                    this.streetAddress = this.initialShippingAddress?.streetAddress;
                    this.zipcode = this.initialShippingAddress?.zipcode;
                    this.city = this.initialShippingAddress?.city;
                    this.stateId = this.initialShippingAddress?.state?.id;
                    this.practitioner = this.modalData?.practitioner?.name;
                    this.practitionerEmail = this.modalData?.practitioner?.email;
                }
            },
            immediate: true,
        },
    },
};
</script>
